import { useState, useEffect, useRef, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import axios from "axios";
import { toast } from "sonner";
import { FilePdf, FileXls, Image as ImageIcon, CloudArrowUp, Trash, Eye, Files, DownloadSimple, EnvelopeSimple, WhatsappLogo, MicrosoftOutlookLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get full URL for documents (handles old relative URLs and new Cloudinary URLs)
const getDocumentUrl = (url) => {
  if (!url) return '';
  // If it's already a full URL (Cloudinary or other), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a relative URL starting with /api/, prepend the backend URL
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  // Otherwise, assume it's relative to the API
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const getDocumentIcon = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'xlsx' || ext === 'xls') return { Icon: FileXls, color: 'text-emerald-600', bg: 'bg-emerald-50' };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return { Icon: ImageIcon, color: 'text-blue-500', bg: 'bg-blue-50' };
  return { Icon: FilePdf, color: 'text-red-500', bg: 'bg-red-50' };
};

// Tenta partilhar o FICHEIRO real (não o link) através da Web Share API nativa do dispositivo -
// o utilizador escolhe a app (Email, Outlook, WhatsApp...) e o ficheiro vai anexado de verdade.
// Não é possível anexar um ficheiro remoto a um link mailto:/ms-outlook: (limitação da plataforma
// web), por isso quando a partilha nativa não está disponível (ex: browser de computador) o
// ficheiro é descarregado e o utilizador tem de o anexar manualmente.
const shareFileNative = async (url, filename, title) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: title || filename });
      return true;
    }
  } catch (error) {
    if (error?.name === 'AbortError') return true;
    console.error('Partilha nativa falhou:', error);
  }
  return false;
};

const downloadFileForManualAttach = (url, filename, appLabel) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  toast.info(`Ficheiro descarregado - anexe-o manualmente no ${appLabel}`);
};

const shareByEmail = async (doc) => {
  const url = getDocumentUrl(doc.url);
  const shared = await shareFileNative(url, doc.name, `Documento: ${doc.name}`);
  if (shared) return;
  downloadFileForManualAttach(url, doc.name, 'seu cliente de email');
  window.location.href = `mailto:?subject=${encodeURIComponent(`Documento: ${doc.name}`)}`;
};

const shareByOutlook = async (doc) => {
  const url = getDocumentUrl(doc.url);
  const shared = await shareFileNative(url, doc.name, `Documento: ${doc.name}`);
  if (shared) return;
  downloadFileForManualAttach(url, doc.name, 'Outlook');
  const subject = encodeURIComponent(`Documento: ${doc.name}`);
  const appUrl = `ms-outlook://compose?subject=${subject}`;
  const mailtoUrl = `mailto:?subject=${subject}`;
  const timer = setTimeout(() => {
    if (!document.hidden) {
      window.location.href = mailtoUrl;
    }
  }, 1200);
  document.addEventListener('visibilitychange', () => clearTimeout(timer), { once: true });
  window.location.href = appUrl;
};

const shareByWhatsapp = (doc) => {
  const url = getDocumentUrl(doc.url);
  const text = encodeURIComponent(`Documento: ${doc.name}\n${url}`);
  window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
};

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const docsRes = await axios.get(`${API}/documents`);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (limit to 150MB)
    const maxSize = 150 * 1024 * 1024; // 150MB
    if (file.size > maxSize) {
      toast.error("Arquivo muito grande. Máximo 150MB.");
      return;
    }

    setUploading(true);
    try {
      // Upload directly to server (better for PDFs)
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 minutes timeout for large files
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      toast.success("Documento carregado com sucesso");
      fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 413) {
        toast.error("Arquivo muito grande para o servidor");
      } else if (error.code === 'ECONNABORTED') {
        toast.error("Timeout - arquivo muito grande ou conexão lenta");
      } else {
        toast.error("Erro ao carregar documento");
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Tem certeza que deseja eliminar este documento?")) return;

    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success("Documento eliminado");
      fetchDocuments();
    } catch (error) {
      toast.error("Erro ao eliminar documento");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <PageHeader
        icon={Files}
        title="Documentos de Merchandising"
        actions={
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              accept=".pdf,.xlsx,.xls,image/*"
              className="hidden"
            />
            <Button
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="btn-upload-document"
            >
              <CloudArrowUp size={18} />
              {uploading ? "A carregar..." : "Carregar Documento"}
            </Button>
          </>
        }
      />

      {/* Documents Grid */}
      <div className="gresart-card">
        {documents.length === 0 ? (
          <div className="empty-state py-16">
            <Files size={64} className="text-stone-300 mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">Sem documentos</h3>
            <p className="text-stone-500 mb-6 max-w-md">
              Carregue catálogos PDF de merchandising para ter acesso rápido aos materiais de vendas
            </p>
            <Button 
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              data-testid="btn-upload-first-document"
            >
              <CloudArrowUp size={18} />
              Carregar Primeiro Documento
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {documents.map((doc) => {
              const { Icon: DocIcon, color: docColor, bg: docBg } = getDocumentIcon(doc.name);
              return (
              <div 
                key={doc.id} 
                className="border border-stone-200 rounded-xl p-5 hover:shadow-lg transition-all duration-200 bg-white"
                data-testid={`document-${doc.id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`p-3 ${docBg} rounded-xl flex-shrink-0`}>
                    <DocIcon size={40} className={docColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-900 truncate text-sm" title={doc.name}>
                      {doc.name}
                    </p>
                    <p className="text-xs text-stone-400 mt-1">
                      {new Date(doc.uploaded_at).toLocaleDateString('pt-PT', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2 mb-2">
                  <a
                    href={getDocumentUrl(doc.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 text-sm py-2.5 px-3 bg-[#00A19A] text-white rounded-lg hover:bg-[#008B85] transition-colors font-medium"
                    data-testid={`btn-view-document-${doc.id}`}
                  >
                    <Eye size={16} />
                    Ver
                  </a>
                  <a
                    href={getDocumentUrl(doc.url)}
                    download={doc.name}
                    className="flex items-center justify-center gap-2 text-sm py-2.5 px-3 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors"
                    data-testid={`btn-download-document-${doc.id}`}
                  >
                    <DownloadSimple size={16} />
                  </a>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="flex items-center justify-center gap-2 text-sm py-2.5 px-3 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors"
                    data-testid={`btn-delete-document-${doc.id}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => shareByEmail(doc)}
                    className="flex-1 flex items-center justify-center py-1.5 text-sky-500 hover:bg-sky-50 rounded-md transition-colors"
                    data-testid={`btn-share-email-${doc.id}`}
                    title="Partilhar por Email"
                  >
                    <EnvelopeSimple size={15} />
                  </button>
                  <button
                    onClick={() => shareByOutlook(doc)}
                    className="flex-1 flex items-center justify-center py-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    data-testid={`btn-share-outlook-${doc.id}`}
                    title="Partilhar por Outlook"
                  >
                    <MicrosoftOutlookLogo size={15} />
                  </button>
                  <button
                    onClick={() => shareByWhatsapp(doc)}
                    className="flex-1 flex items-center justify-center py-1.5 text-green-500 hover:bg-green-50 rounded-md transition-colors"
                    data-testid={`btn-share-whatsapp-${doc.id}`}
                    title="Partilhar por WhatsApp"
                  >
                    <WhatsappLogo size={15} />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
